// External dependencies
import { jsx as _jsx } from "react/jsx-runtime";
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import Typography from '@tiptap/extension-typography';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { memo, useEffect, useRef } from 'react';
// Internal utilities
import { cn } from '../../lib/utils';
export const TextArtifactEditor = /*#__PURE__*/ memo(function TextArtifactEditor({ value, className, onChange, autoSyncWhenUnfocused = true }) {
    const isSyncingRef = useRef(false);
    const editor = useEditor({
        extensions: [
            StarterKit,
            Typography,
            Placeholder.configure({
                placeholder: 'Start editing…'
            }),
            Table.configure({
                resizable: true,
                HTMLAttributes: {
                    class: 'editor-table'
                }
            }),
            TableRow.configure({
                HTMLAttributes: {
                    class: 'editor-table-row'
                }
            }),
            TableHeader.configure({
                HTMLAttributes: {
                    class: 'editor-table-header'
                }
            }),
            TableCell.configure({
                HTMLAttributes: {
                    class: 'editor-table-cell'
                }
            })
        ],
        content: '',
        editorProps: {
            attributes: {
                class: ''
            },
            handleKeyDown: (_view, event)=>{
                const e = event;
                const isMod = navigator.platform.includes('Mac') ? e.metaKey : e.ctrlKey;
                if (isMod && (e.key === 's' || e.key === 'S')) {
                    e.preventDefault();
                    onChange?.(editor?.getHTML() || '');
                    return true;
                }
                return false;
            }
        },
        onUpdate: ({ editor })=>{
            // Don't trigger onChange during programmatic content sync
            if (!isSyncingRef.current) {
                onChange?.(editor.getHTML());
            }
        },
        immediatelyRender: false
    });
    useEffect(()=>{
        if (!editor || autoSyncWhenUnfocused && editor.isFocused) return;
        isSyncingRef.current = true;
        try {
            const rawHtml = marked.parse(value, {
                async: false
            }) || '';
            const sanitizedHtml = DOMPurify.sanitize(rawHtml);
            editor.commands.setContent(sanitizedHtml, {
                parseOptions: {
                    preserveWhitespace: 'full'
                }
            });
        } catch (error) {
            console.warn('Failed to parse markdown:', error);
            editor.commands.setContent(value);
        }
        // Reset flag after frame to let ProseMirror process
        requestAnimationFrame(()=>{
            isSyncingRef.current = false;
        });
    }, [
        editor,
        value,
        autoSyncWhenUnfocused
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: cn('relative', className),
        children: /*#__PURE__*/ _jsx(EditorContent, {
            editor: editor,
            className: "prose-editor-wrapper"
        })
    });
});
